import { useRouter } from '@nuxtjs/composition-api';
import request from '@/utils/api';
import { useStore } from '@/compositions/store';

import { Search } from '@/compositions/base-search';
import { Save } from '@/compositions/base-save';
import { requestApi } from '@/apis';

import { IAdminCompanyregisterForm, IAdminCompanyChargeForm } from '@/types';
import {
  IChargeHistoryResponse,
  ICompanyCreateBulkForm,
  ICompanyCreateAllRequest,
  IAichatBaCompanyNeedsRequestBody,
  IAichatBaCompanyNeedsResponse,
  IAichatBaCompanyNeedsWritingStyleResponse,
  IAichatBaNeedsOfferResponse,
  IAichatBaWebsiteVisionRequestBody,
  IAichatBaWebsiteBusinessRequestBody,
  IAichatBaWebsiteCompanyConceptRequestBody,
  IAichatBaWebsiteCompanyRepRequestBody,
  IAichatBaWebsiteWritingStyleResponse,
  IAichatBaWebsiteVisionResponse,
  IAichatBaWebsiteBusinessResponse,
  IAichatBaWebsiteCompanyRepResponse,
  IAichatBaWebsiteCompanyConceptResponse,
  IAichatBaMatchingProfileRequestBody,
  IAichatBaMatchingProfileResponse,
  IAichatBaMatchingProfileWritingStyleResponse,
  IAdminCompanyChargeEditForm,
  INewsResponse,
  INewsRecentResponse,
  IChargePlanResponse,
  IChargeChangePlanResponse,
  ChargePlanType
} from '@/types/xba';
import { IMyCompanyResult, ISettingPermissions } from '@/types/api/xba';
import { ICouponsAwaitingApprovalResponse, ICouponSearchParams } from '@/types/api/xba/coupon';
import { IAdminHgbaPortalCompanyregisterForm, IBaHgbaPortalForm } from '@/types/portal/hgba';

type IXbaApis = {
  postXbaCompanyRegisterApproved: (form: IAdminCompanyregisterForm) => Promise<void>;
  postXbaCompanyRegisterRejected: (form: IAdminCompanyregisterForm) => Promise<void>;
  postHgbaPortalCompanyRegisterRejected: (hgbaPortalForm: IAdminHgbaPortalCompanyregisterForm) => Promise<void>;
  postChargeHistory: (companyId: number) => Promise<IChargeHistoryResponse>;
  postCompanyChargeSave: (form: IAdminCompanyChargeEditForm, password: string) => Promise<void>;
  postChargeSave: (form: IAdminCompanyChargeForm, password: string) => Promise<void>;
  postXbaCompanyUnsubscribe: (form: any) => Promise<void>;
  postHgbaPortalOnlyUnsubscribe: (form: any) => Promise<void>;
  postCompanyCreateAll: (form: ICompanyCreateBulkForm) => Promise<void>;
  getMyCompany: () => Promise<IMyCompanyResult>;
  getXbaSettingPermissions: () => Promise<ISettingPermissions>;
  postAichatBaCompanyNeeds: (form: IAichatBaCompanyNeedsRequestBody) => Promise<IAichatBaCompanyNeedsResponse>;
  getAichatBaCompanyNeedsWritingStyle: () => Promise<IAichatBaCompanyNeedsWritingStyleResponse>;
  postAichatBaWebsiteVision: (form: IAichatBaWebsiteVisionRequestBody) => Promise<IAichatBaWebsiteVisionResponse>;
  postAichatBaWebsiteBusiness: (form: IAichatBaWebsiteBusinessRequestBody) => Promise<IAichatBaWebsiteBusinessResponse>;
  postAichatBaWebsiteCompanyRep: (form: IAichatBaWebsiteCompanyRepRequestBody) => Promise<IAichatBaWebsiteCompanyRepResponse>;
  postAichatBaWebsiteCompanyConcept: (form: IAichatBaWebsiteCompanyConceptRequestBody) => Promise<IAichatBaWebsiteCompanyConceptResponse>;
  getAichatBaWebsiteWritingStyle: () => Promise<IAichatBaWebsiteWritingStyleResponse>;
  getXbaUpgradeStatus: () => Promise<any>;
  postHgbaCompanyRegisterUpgrade: (form: IBaHgbaPortalForm) => Promise<'credit_payment' | 'normal' | ''>;
  postAichatBaNeedsOffer: (needsUniqueCode: string) => Promise<IAichatBaNeedsOfferResponse>;
  getXbaCsoSearch: (params: ICouponSearchParams) => Promise<ICouponsAwaitingApprovalResponse>;
  getNewsSearch: (conditions: { rows: number; page: number; }) => Promise<INewsResponse>;
  getNewsRecent: () => Promise<INewsRecentResponse>;
  postAichatBaMatchingProfile: (form: IAichatBaMatchingProfileRequestBody) => Promise<IAichatBaMatchingProfileResponse>;
  getAichatBaMatchingProfileWritingStyle: () => Promise<IAichatBaMatchingProfileWritingStyleResponse>;
  getXbaChargePlan: () => Promise<IChargePlanResponse>;
  getXbaChargeChangePlan: () => Promise<IChargeChangePlanResponse>;
  postXbaChargeChangePlan: (companyUniqueCode: string, nextChargePeriodStartDate: string, chargeType: ChargePlanType | null) => Promise<void>
}

/**
 * XBA APIs
 */
export const xbaApis = (): IXbaApis => {
  // 登録共通モジュールを利用
  const { saveData, setSaveConfig, saveError, error } = Save();
  const { addToastMessage, setOverlay } = useStore();
  const router = useRouter();
  const { setSearchConfig, loadData } = Search();

  const postXbaCompanyRegisterApproved = async (form: IAdminCompanyregisterForm): Promise<void> => {
    setSaveConfig('/api/v1/companyregister/approved', {
      success: '申請を承認しました',
      error: '申請の承認に失敗しました',
      useResponseMessage: true,
      doToast: true
    });
    try {
      await saveData({ data: form });
    } catch (e) {
      throw new Error('申請の承認に失敗しました');
    }
    if (saveError.value) {
      throw new Error(error.value.body);
    }
  };

  const postXbaCompanyRegisterRejected = async (form: IAdminCompanyregisterForm): Promise<void> => {
    setSaveConfig('/api/v1/companyregister/rejected', {
      success: '申請を却下しました',
      error: '申請の却下に失敗しました',
      useResponseMessage: true,
      doToast: true
    });
    try {
      await saveData({ data: form });
    } catch (e) {
      throw new Error('申請の却下に失敗しました');
    }
    if (saveError.value) {
      throw new Error(error.value.body);
    }
  };

  const postHgbaPortalCompanyRegisterRejected = async (hgbaPortalForm: IAdminHgbaPortalCompanyregisterForm): Promise<void> => {
    setSaveConfig('/api/v1/hgba/portal/companyregister/rejected', {
      success: '申請を却下しました',
      error: '申請の却下に失敗しました',
      useResponseMessage: true,
      doToast: true
    });
    try {
      await saveData({ data: hgbaPortalForm });
    } catch (e) {
      throw new Error('申請の却下に失敗しました');
    }
    if (saveError.value) {
      throw new Error(error.value.body);
    }
  };

  /**
   * BAの退会予約
   */
  const postXbaCompanyUnsubscribe = async (form: any): Promise<void> => {
    setSaveConfig('/api/v1/company/unsubscribe', {
      success: '退会予約を行いました',
      error: '退会予約に失敗しました',
      useResponseMessage: true,
      doToast: true
    });
    const r: any = await saveData({ data: form });
    if (r && r.status && r.status === 'error') {
      const message = r.status === 'error' ? '退会予約に失敗しました' : 'エラーが発生しました';
      addToastMessage(message, 'error');
      throw new Error(message);
    }
  };

  /**
   * 肥後銀行のポータルにしか加入していない場合の退会予約
   */
  const postHgbaPortalOnlyUnsubscribe = async (form: any): Promise<void> => {
    setSaveConfig('/api/v1/hgba/portal/company/unsubscribe', {
      success: '退会予約を行いました',
      error: '退会予約に失敗しました',
      useResponseMessage: true,
      doToast: true
    });
    const r: any = await saveData({ data: form });
    if (r && r.status && r.status === 'error') {
      const message = r.status === 'error' ? '退会予約に失敗しました' : 'エラーが発生しました';
      addToastMessage(message, 'error');
      throw new Error(message);
    }
  };

  /**
   * 課金情報の保存API
   * パスワード認証有
   */
  const postCompanyChargeSave = async (
    form: IAdminCompanyChargeEditForm,
    password: string
  ): Promise<void> => {
    const endpoint = '/api/v1/company/charge/save';

    const payload = {
      ...form,
      password
    };

    setOverlay(true);

    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: payload
      });

      if (!response) {
        throw new Error('認証に失敗しました。再度お試しください');
      } else if (response.status !== 'success') {
        const errorMessage = response?.message?.body || response?.message || 'システムエラーが発生しました';
        throw new Error(errorMessage);
      }

      addToastMessage('保存しました');
    } finally {
      setOverlay(false);
    }
  };

  /**
   * 課金履歴を取得するAPI
   */
  const postChargeHistory = async (companyId: number): Promise<IChargeHistoryResponse> => {
    const endpoint = '/api/v1/charge/history';

    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: {
          charge_company_id: companyId
        }
      });

      if (!response || !response.result || response.status !== 'success') {
        throw new Error('課金履歴のレスポンスが不正です。');
      }

      return response.result as IChargeHistoryResponse;
    } catch (error) {
      console.error('課金履歴の取得に失敗しました。', error);
      throw error;
    }
  };

  /**
   * 課金データの保存API
   * パスワード認証有
   */
  const postChargeSave = async (
    form: IAdminCompanyChargeForm,
    password: string
  ): Promise<void> => {
    const endpoint = '/api/v1/charge/save';

    const payload = {
      ...form,
      password
    };

    setOverlay(true);

    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: payload
      });

      if (!response) {
        throw new Error('認証に失敗しました。再度お試しください');
      } else if (response.status !== 'success') {
        const errorMessage = response?.message?.body || response?.message || 'システムエラーが発生しました';
        throw new Error(errorMessage);
      }

      addToastMessage('保存しました');
    } finally {
      setOverlay(false);
    }
  };

  /**
   * 企業一括登録用のAPI
   */
  const postCompanyCreateAll = async (form: ICompanyCreateBulkForm): Promise<void> => {
    const endpoint = '/api/v1/company/create_all';

    setOverlay(true);

    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: form as ICompanyCreateAllRequest
      });

      if (response?.result === null) {
        addToastMessage('保存しました');
        router.push('/admin/company');
        return;
      }

      if (response?.result?.status === 'error') {
        addToastMessage('保存に失敗しました', 'error');
        return;
      }

      addToastMessage('エラーが発生しました', 'error');
    } catch (error) {
      addToastMessage('エラーが発生しました', 'error');
    } finally {
      setOverlay(false);
    }
  };

  /**
   * 全ユーザータイプの権限設定取得
   */
  const getXbaSettingPermissions = async (): Promise<ISettingPermissions> => {
    // 権限データ取得API
    const permissionEndpoint = '/api/v1/setting/permissions';
    setSearchConfig(permissionEndpoint, {
      method: 'get',
      ignorePageing: true
    });
    const result: ISettingPermissions = await loadData();
    return result;
  };

  /**
   * 企業情報取得API
   */
  const getMyCompany = async (): Promise<IMyCompanyResult> => {
    const response: any = await request({
      url: '/api/v1/company/mycompany',
      method: 'get'
    });
    if (response?.status !== 'success' || !response?.result?.company) {
      throw new Error('企業情報の取得に失敗しました。');
    }
    return response.result;
  };

  /**
   * AIアシストニーズ生成API
   */
  const postAichatBaCompanyNeeds = async (form: IAichatBaCompanyNeedsRequestBody): Promise<IAichatBaCompanyNeedsResponse> => {
    const endpoint = '/api/v1/aichat/ba/company/needs';

    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: form as IAichatBaCompanyNeedsRequestBody
      });

      if (!response || !response.result || response.status !== 'success') {
        throw new Error('AIアシストニーズ生成APIのレスポンスが不正です。');
      }

      const data = response.result as IAichatBaCompanyNeedsResponse;
      return data;
    } catch (error) {
      console.error('AIアシストニーズ生成に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * writing_style取得API
   */
  const getAichatBaCompanyNeedsWritingStyle = async (): Promise<IAichatBaCompanyNeedsWritingStyleResponse> => {
    const endpoint = 'api/v1/aichat/ba/company/needs/writing_styles';

    try {
      const response: any = await request({
        url: endpoint,
        method: 'get'
      });

      if (!response || response.status !== 'success') {
        throw new Error('writing_style取得APIのレスポンスが不正です。');
      }

      const data = response.result as IAichatBaCompanyNeedsWritingStyleResponse;
      return data;
    } catch (error) {
      console.error('writing_style取得APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * AIアシストキャッチコピー生成API
   */
  const postAichatBaWebsiteVision = async (form: IAichatBaWebsiteVisionRequestBody): Promise<IAichatBaWebsiteVisionResponse> => {
    const endpoint = '/api/v1/aichat/ba/website/vision';
    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: form
      });

      if (!response || !response.result || response.status !== 'success') {
        throw new Error('AIアシスト生成APIのレスポンスが不正です。');
      }

      const data = response.result as IAichatBaWebsiteVisionResponse;
      return data;
    } catch (error) {
      console.error('AIアシストキャッチコピー生成APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * AIアシスト事業概要・詳細生成API
   */
  const postAichatBaWebsiteBusiness = async (form: IAichatBaWebsiteBusinessRequestBody): Promise<IAichatBaWebsiteBusinessResponse> => {
    const endpoint = '/api/v1/aichat/ba/website/business';
    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: form
      });
      if (!response || !response.result || response.status !== 'success') {
        throw new Error('AIアシスト生成APIのレスポンスが不正です。');
      }

      const data = response.result as IAichatBaWebsiteBusinessResponse;
      return data;
    } catch (error) {
      console.error('AIアシスト事業概要・詳細生成APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * AIアシスト代表挨拶生成API
   */
  const postAichatBaWebsiteCompanyRep = async (form: IAichatBaWebsiteCompanyRepRequestBody): Promise<IAichatBaWebsiteCompanyRepResponse> => {
    const endpoint = '/api/v1/aichat/ba/website/company_rep';
    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: form
      });
      if (!response || !response.result || response.status !== 'success') {
        throw new Error('AIアシスト生成APIのレスポンスが不正です。');
      }

      const data = response.result as IAichatBaWebsiteCompanyRepResponse;
      return data;
    } catch (error) {
      console.error('AIアシスト代表挨拶生成APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * AIアシスト企業理念生成API
   */
  const postAichatBaWebsiteCompanyConcept = async (form: IAichatBaWebsiteCompanyConceptRequestBody): Promise<IAichatBaWebsiteCompanyConceptResponse> => {
    const endpoint = '/api/v1/aichat/ba/website/company_concept';
    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: form
      });
      if (!response || !response.result || response.status !== 'success') {
        throw new Error('AIアシスト生成APIのレスポンスが不正です。');
      }

      const data = response.result as IAichatBaWebsiteCompanyConceptResponse;
      return data;
    } catch (error) {
      console.error('AIアシスト企業理念生成APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * Website用 writing_style取得API
   */
  const getAichatBaWebsiteWritingStyle = async (): Promise<IAichatBaWebsiteWritingStyleResponse> => {
    const endpoint = '/api/v1/aichat/ba/website/writing_styles';

    try {
      const response: any = await request({
        url: endpoint,
        method: 'get'
      });
      if (!response || !response.result || response.status !== 'success') {
        throw new Error('writing_style取得APIのレスポンスが不正です。');
      }

      return response.result as IAichatBaWebsiteWritingStyleResponse;
    } catch (error) {
      console.error('writing_style取得APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * AIアシストビジネスマッチング基本情報生成API
   */
  const postAichatBaMatchingProfile = async (form: IAichatBaMatchingProfileRequestBody): Promise<IAichatBaMatchingProfileResponse> => {
    const endpoint = '/api/v1/aichat/ba/company/matching_profile';
    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: form
      });

      if (!response || !response.result || response.status !== 'success') {
        throw new Error('AIアシストビジネスマッチング基本情報生成APIのレスポンスが不正です。');
      }

      return response.result as IAichatBaMatchingProfileResponse;
    } catch (error) {
      console.error('AIアシストビジネスマッチング基本情報生成APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * ビジネスマッチング基本情報生成用 writing_style取得API
   */
  const getAichatBaMatchingProfileWritingStyle = async (): Promise<IAichatBaMatchingProfileWritingStyleResponse> => {
    const endpoint = '/api/v1/aichat/ba/company/writing_styles';
    try {
      const response: any = await request({
        url: endpoint,
        method: 'get'
      });

      if (!response || !response.result || response.status !== 'success') {
        throw new Error('writing_style取得APIのレスポンスが不正です。');
      }

      return response.result as IAichatBaMatchingProfileWritingStyleResponse;
    } catch (error) {
      console.error('writing_style取得APIの取得に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /**
   * アップグレードステータス取得
   */
  const getXbaUpgradeStatus = async (): Promise<any> => {
    setSearchConfig('/api/v1/companyregister/upgrade_status', {
      ignorePageing: true,
      method: 'get'
    });
    const r: any = await loadData();
    if (r && r.status && r.status === 'error') {
      const message = 'エラーが発生しました';
      addToastMessage(message, 'error');
      throw new Error(message);
    }
    return r;
  };

  /**
   * 肥銀ポータルからBA会員へアップグレード
   */
  const postHgbaCompanyRegisterUpgrade = async (form: IBaHgbaPortalForm): Promise<'credit_payment' | 'normal' | ''> => {
    setSaveConfig('/api/v1/hgba/portal/companyregister/upgrade', {
      useResponseMessage: true,
      doToast: false,
      successWithMessage: true
    });
    await saveData({ data: form });
    if (!saveError.value) {
      return '';
    }
    addToastMessage(error.value.message, 'error');
    return error.value.type;
  };
  /**
   * 商談依頼AIアシスト API
   */
  const postAichatBaNeedsOffer = async (needsUniqueCode: string): Promise<IAichatBaNeedsOfferResponse> => {
    const endpoint = '/api/v1/aichat/ba/needs_offer';
    try {
      const response: any = await request({
        url: endpoint,
        method: 'post',
        data: {
          needs_unique_code: needsUniqueCode
        }
      });
      if (!response || !response.result || response.status !== 'success') {
        throw new Error('商談依頼AIアシストAPIのレスポンスが不正です。');
      }

      if (!response.result.message || response.result.message.trim() === '') {
        throw new Error('商談依頼AIアシストが文章を生成できませんでした。');
      }

      return response.result as IAichatBaNeedsOfferResponse;
    } catch (error) {
      console.error('AI商談依頼 生成に失敗しました。', error);
      throw new Error('エラーが発生しました');
    }
  };

  /** クーポンを取得するAPI */
  const getXbaCsoSearch = async (params: ICouponSearchParams): Promise<ICouponsAwaitingApprovalResponse> => {
    const endpoint = '/api/v1/cso/search';

    try {
      const response: any = await request({
        url: endpoint,
        method: 'get',
        params
      });

      if (!response.result) {
        return { list: [] };
      }

      return response.result as ICouponsAwaitingApprovalResponse;
    } catch (error) {
      console.error('クーポンの取得に失敗しました。', error);
      addToastMessage('クーポンの取得に失敗しました。', 'error');
      throw error;
    }
  };

  /** ニュース取得API */
  const getNewsSearch = async (conditions: { rows: number; page: number; }): Promise<INewsResponse> => {
    const endpoint = '/api/v1/news/search';

    setOverlay(true);

    try {
      const response: any = await request({
        url: endpoint,
        method: 'get',
        params: conditions
      });

      return response.result as INewsResponse;
    } catch (error) {
      console.error('ニュースの取得に失敗しました。', error);
      addToastMessage('ニュースの取得に失敗しました。', 'error');
      throw error;
    } finally {
      setOverlay(false);
    }
  };

  /** 直近ニュース取得API */
  const getNewsRecent = async (): Promise<INewsRecentResponse> => {
    const endpoint = '/api/v1/news/recent';

    setOverlay(true);

    try {
      const response: any = await request({
        url: endpoint,
        method: 'get'
      });

      return response.result as INewsRecentResponse;
    } catch (error) {
      console.error('ニュースの取得に失敗しました。', error);
      addToastMessage('ニュースの取得に失敗しました。', 'error');
      throw error;
    } finally {
      setOverlay(false);
    }
  };

  /** 契約プラン取得API */
  const getXbaChargePlan = async (): Promise<IChargePlanResponse> => {
    try {
      const data = await requestApi('/api/v1/charge/plan', {}, 'get');

      if (data.status !== 'success') {
        throw new Error(data?.result?.error_messages || '契約プランの取得に失敗しました');
      }

      return data;
    } catch (error) {
      console.error('契約プランの取得に失敗しました', error);
      throw error;
    }
  };

  /** 契約プラン変更情報取得API */
  const getXbaChargeChangePlan = async (): Promise<IChargeChangePlanResponse> => {
    try {
      const data = await requestApi('/api/v1/charge/change_plan', {}, 'get');

      if (data.status !== 'success') {
        throw new Error(data?.result?.error_messages || '契約プラン変更情報の取得に失敗しました');
      }

      return data;
    } catch (error: any) {
      console.error('契約プラン変更情報の取得に失敗しました', error);
      throw error;
    }
  };

  /** 契約プラン変更API */
  const postXbaChargeChangePlan = async (
    companyUniqueCode: string,
    chargePeriodStartDate: string,
    updatedChargeType: ChargePlanType | null
  ): Promise<void> => {
    try {
      const parameters = {
        company_unique_code: companyUniqueCode,
        next_charge_period_start_date: chargePeriodStartDate,
        charge_type: updatedChargeType
      };

      const data = await requestApi('/api/v1/charge/change_plan', parameters, 'post');

      if (data.status !== 'success') {
        throw new Error(data?.result?.error_messages || '契約プランの変更に失敗しました。');
      }
    } catch (error) {
      console.error('契約プランの変更に失敗しました。', error);

      throw error;
    }
  };

  return {
    postXbaCompanyRegisterApproved,
    postXbaCompanyRegisterRejected,
    postHgbaPortalCompanyRegisterRejected,
    postCompanyChargeSave,
    postChargeSave,
    postChargeHistory,
    postXbaCompanyUnsubscribe,
    postHgbaPortalOnlyUnsubscribe,
    postCompanyCreateAll,
    getMyCompany,
    getXbaSettingPermissions,
    postAichatBaCompanyNeeds,
    getAichatBaCompanyNeedsWritingStyle,
    postAichatBaWebsiteVision,
    postAichatBaWebsiteBusiness,
    postAichatBaWebsiteCompanyConcept,
    postAichatBaWebsiteCompanyRep,
    getAichatBaWebsiteWritingStyle,
    postAichatBaMatchingProfile,
    getAichatBaMatchingProfileWritingStyle,
    getXbaUpgradeStatus,
    postHgbaCompanyRegisterUpgrade,
    postAichatBaNeedsOffer,
    getXbaCsoSearch,
    getNewsSearch,
    getNewsRecent,
    getXbaChargePlan,
    getXbaChargeChangePlan,
    postXbaChargeChangePlan
  };
};
